/* =========================================================================
   persistence.ts — Abstraction layer for localStorage + Supabase sync.
   ========================================================================= */

import { supabase, isSupabaseConfigured } from "./supabase-client";
import { toastGlobal } from "./toast";
import { AUDIT_LOG_CAP, VERSION_PAGE_SIZE } from "./platform-types";
import type { Scenario, BuilderQuote, AuditEntry } from "./types";
import type {
  ScenarioVersion,
  ScenarioVersionMeta,
  ComputeFingerprint,
  Site,
} from "./platform-types";
import { migrateLegacyBuilderQuotes } from "./ws-data/json-export";
import { isDoltBackend } from "./dolt-mode";
import {
  readDoltPersisted,
  readDoltSites,
  saveDoltLocal,
  saveDoltSitesLocal,
} from "./dolt-persistence";

const LS_KEY = "stratosfyre-scenarios-v1";
const LS_ACTIVE = "stratosfyre-active-id";
export const LS_SITES_KEY = "stratosfyre-sites-v1";

export interface PersistedState {
  scenarios: Scenario[];
  activeId: string;
}

function loadLocal(): PersistedState | null {
  try {
    const raw = localStorage.getItem(LS_KEY);
    const activeRaw = localStorage.getItem(LS_ACTIVE);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return null;
    const scenarios = parsed.filter(isValidScenario);
    if (scenarios.length === 0) return null;
    const activeId =
      typeof activeRaw === "string" &&
      scenarios.some((s: Scenario) => s.id === activeRaw)
        ? activeRaw
        : scenarios[0].id;
    return { scenarios, activeId };
  } catch {
    return null;
  }
}

function saveLocal(scenarios: Scenario[], activeId: string): boolean {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(scenarios));
    localStorage.setItem(LS_ACTIVE, activeId);
    return true;
  } catch {
    // Storage quota exceeded or private mode
    return false;
  }
}

export function isValidScenario(s: unknown): s is Scenario {
  if (!s || typeof s !== "object") return false;
  const o = s as Record<string, unknown>;
  return (
    typeof o.id === "string" &&
    typeof o.name === "string" &&
    typeof o.assumptions === "object" &&
    o.assumptions !== null &&
    Array.isArray(o.buildings)
  );
}

/** Cap applied to the unbounded Scenario.auditLog (keeps newest AUDIT_LOG_CAP entries). */
export function capAuditLog(log?: AuditEntry[]): AuditEntry[] | undefined {
  if (!log) return log;
  return log.length > AUDIT_LOG_CAP ? log.slice(-AUDIT_LOG_CAP) : log;
}

const SCHEMA_VERSION = 1;

/** Idempotent forward-migration for persisted Scenario blobs. Fills absent platform-foundation
 *  fields so legacy localStorage/Supabase data loads cleanly. */
export function migrateScenarioBlob(s: Scenario): Scenario {
  const builderQuotes = migrateLegacyBuilderQuotes(s.builderQuotes);
  return {
    ...s,
    schemaVersion: s.schemaVersion ?? SCHEMA_VERSION,
    updatedAt: s.updatedAt ?? 0,
    // A missing method is a legacy scenario. Do not infer dated-v2 from a
    // partial blob: it would route a legacy record into a different engine.
    calculationMethod: s.calculationMethod ?? "legacy-v1",
    auditLog: capAuditLog(s.auditLog),
    ...(builderQuotes ? { builderQuotes } : {}),
    assumptions: {
      ...s.assumptions,
      coveredPlayAreaGroundRent: s.assumptions.coveredPlayAreaGroundRent ?? 0,
      coveredPlayAreaConstructionCost:
        s.assumptions.coveredPlayAreaConstructionCost ?? 0,
    },
  };
}

// ---------- Sync error handling ----------

function isIgnoredError(error: { code?: string; message: string }): boolean {
  return (
    error.code === "403" ||
    error.code === "404" ||
    error.message.includes("permission denied") ||
    error.message.includes("Could not find")
  );
}

/** Tracks already-surfaced ignored errors so a persistent misconfiguration is
 *  logged once for diagnosis instead of on every load/save/retry. */
const loggedIgnoredKeys = new Set<string>();

/** Surface an ignored (degraded-to-local) sync error so a real misconfiguration
 *  (e.g. a missing RLS grant) is diagnosable in the console rather than silent.
 *  Deduplicated by operation+error so it does not spam the console. */
function logIgnoredSyncError(
  operation: string,
  error: { code?: string; message: string },
): void {
  const key = `${operation}:${error.code ?? ""}:${error.message}`;
  if (loggedIgnoredKeys.has(key)) return;
  loggedIgnoredKeys.add(key);
  console.warn(
    `[sync] ${operation}: degraded to local-only — ${[error.code, error.message].filter(Boolean).join(" ")}`,
  );
}

/** Test-only helper to reset the ignored-error dedupe set. */
export function __resetIgnoredLogForTests() {
  loggedIgnoredKeys.clear();
}

let lastToastTime = 0;
const TOAST_DEBOUNCE_MS = 5000;
let RETRY_DELAY_MS = 800;

function notifySyncError(message: string) {
  const now = Date.now();
  if (now - lastToastTime < TOAST_DEBOUNCE_MS) return;
  lastToastTime = now;
  toastGlobal(message, "warning", 6000);
}

async function execWithRetry(
  operation: string,
  fn: () => PromiseLike<any>,
  retries = 1,
): Promise<any> {
  let result = await fn();
  if (!result.error) return result;
  if (isIgnoredError(result.error)) {
    logIgnoredSyncError(operation, result.error);
    return result;
  }

  for (let i = 0; i < retries; i++) {
    await new Promise((r) => setTimeout(r, RETRY_DELAY_MS * (i + 1)));
    result = await fn();
    if (!result.error) return result;
    if (isIgnoredError(result.error)) {
      logIgnoredSyncError(operation, result.error);
      return result;
    }
  }

  notifySyncError(`Sync failed (${operation}): ${result.error.message}`);
  return result;
}

/** Test-only helper to reset toast debounce state. */
export function __resetSyncDebounceForTests() {
  lastToastTime = 0;
}

/** Test-only helper to set retry delay to 0 for fast tests. */
export function __setRetryDelayForTests(ms: number) {
  RETRY_DELAY_MS = ms;
}

// ---------- Raw Supabase operations (no retry / toast) ----------

async function upsertScenarioRaw(scenario: Scenario): Promise<any> {
  if (isDoltBackend()) return { data: null, error: null };
  return supabase.from("scenarios").upsert(
    {
      id: scenario.id,
      name: scenario.name,
      data: scenario as unknown as Record<string, unknown>,
      updated_at: new Date().toISOString(),
      project_id: scenario.siteId ?? null,
    },
    { onConflict: "id" },
  );
}

async function syncBuilderQuotesRaw(
  scenarioId: string,
  quotes: BuilderQuote[],
): Promise<any> {
  if (isDoltBackend()) return { data: null, error: null };
  return supabase.from("builder_quotes").upsert(
    {
      id: scenarioId,
      data: quotes as unknown as Record<string, unknown>,
      updated_at: new Date().toISOString(),
    },
    { onConflict: "id" },
  );
}

// ---------- Versioning ----------

export type VersionSaveInput = {
  scenarioId: string;
  label: string;
  note?: string;
  snapshot: Scenario;
  schemaVersion: number;
  computeFingerprint?: ComputeFingerprint;
  createdByLabel?: string;
};

function toVersionRow(v: VersionSaveInput): Record<string, unknown> {
  return {
    scenario_id: v.scenarioId,
    label: v.label,
    note: v.note ?? null,
    snapshot: v.snapshot as unknown as Record<string, unknown>,
    schema_version: v.schemaVersion,
    compute_fingerprint: v.computeFingerprint ?? null,
    created_by_label: v.createdByLabel ?? null,
  };
}

function fromVersionRow(row: Record<string, any>): ScenarioVersion {
  return {
    id: row.id,
    scenarioId: row.scenario_id,
    label: row.label,
    note: row.note ?? undefined,
    snapshot: row.snapshot as Scenario,
    schemaVersion: row.schema_version ?? 1,
    computeFingerprint: row.compute_fingerprint ?? undefined,
    createdAt: new Date(row.created_at).getTime(),
    createdBy: row.created_by ?? undefined,
    createdByLabel: row.created_by_label ?? undefined,
  };
}

function fromVersionMetaRow(row: Record<string, any>): ScenarioVersionMeta {
  return {
    id: row.id,
    scenarioId: row.scenario_id,
    label: row.label,
    note: row.note ?? undefined,
    schemaVersion: row.schema_version ?? 1,
    createdAt: new Date(row.created_at).getTime(),
    createdBy: row.created_by ?? undefined,
    createdByLabel: row.created_by_label ?? undefined,
  };
}

async function saveVersionRaw(v: VersionSaveInput): Promise<any> {
  if (isDoltBackend()) return { data: null, error: null };
  return supabase.from("scenario_versions").insert(toVersionRow(v));
}

export function saveVersion(input: VersionSaveInput): void {
  if (isDoltBackend())
    throw new Error("Version persistence is unavailable in Dolt mode");
  syncQueue.enqueue("version", input.scenarioId, input);
}

export async function fetchVersionMetas(
  scenarioId: string,
  opts?: { limit?: number; before?: number },
): Promise<ScenarioVersionMeta[]> {
  if (isDoltBackend()) return [];
  if (!isSupabaseConfigured()) return [];
  const limit = opts?.limit ?? VERSION_PAGE_SIZE;
  let q = supabase
    .from("scenario_versions")
    .select(
      "id, scenario_id, label, note, schema_version, compute_fingerprint, created_by_label, created_at, created_by",
    )
    .eq("scenario_id", scenarioId)
    .order("created_at", { ascending: false })
    .limit(limit);
  if (opts?.before !== undefined) {
    q = q.lt("created_at", new Date(opts.before).toISOString());
  }
  const { data, error } = await execWithRetry("fetchVersionMetas", () => q);
  if (error) return [];
  return (data ?? []).map(fromVersionMetaRow);
}

export async function fetchVersion(
  versionId: string,
): Promise<ScenarioVersion | null> {
  if (isDoltBackend()) return null;
  if (!isSupabaseConfigured()) return null;
  const { data, error } = await execWithRetry("fetchVersion", () =>
    supabase.from("scenario_versions").select("*").eq("id", versionId).single(),
  );
  if (error || !data) return null;
  return fromVersionRow(data);
}

// ---------- Supabase API ----------

export interface ScenarioRecord {
  id: string;
  user_id: string;
  name: string;
  data: Scenario;
  updated_at: string;
  created_at: string;
}

export interface BuilderQuoteRecord {
  id: string;
  user_id: string;
  data: BuilderQuote[];
  updated_at: string;
  created_at: string;
}

export async function fetchScenarios(): Promise<PersistedState | null> {
  if (isDoltBackend()) return null;
  if (!isSupabaseConfigured()) return null;
  const { data, error } = await execWithRetry("fetchScenarios", () =>
    supabase
      .from("scenarios")
      .select("id, data, project_id")
      .order("updated_at", { ascending: false }),
  );
  if (error) return null;

  const scenarios: Scenario[] = [];
  for (const row of data ?? []) {
    if (row.data && isValidScenario(row.data)) {
      const scenario = row.data as Scenario;
      // Live schema uses project_id column as the authoritative site grouping.
      if (row.project_id) scenario.siteId = row.project_id as string;
      scenarios.push(scenario);
    }
  }

  if (scenarios.length === 0) return null;
  return { scenarios, activeId: scenarios[0].id };
}

export async function upsertScenario(scenario: Scenario): Promise<void> {
  if (isDoltBackend()) return;
  if (!isSupabaseConfigured()) return;
  await execWithRetry("upsertScenario", () => upsertScenarioRaw(scenario));
}

export async function deleteScenarioRemote(id: string): Promise<void> {
  if (isDoltBackend()) return;
  if (!isSupabaseConfigured()) return;
  await execWithRetry("deleteScenarioRemote", () =>
    supabase.from("scenarios").delete().eq("id", id),
  );
}

// ---------- Sites (MULTI-SITE) ----------

export function loadSitesLocal(): Site[] {
  if (isDoltBackend()) return readDoltSites();
  try {
    const raw = localStorage.getItem(LS_SITES_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (s): s is Site =>
        s && typeof s.id === "string" && typeof s.name === "string",
    );
  } catch {
    return [];
  }
}

export function saveSitesLocal(sites: Site[]): boolean {
  if (isDoltBackend()) return saveDoltSitesLocal(sites);
  try {
    localStorage.setItem(LS_SITES_KEY, JSON.stringify(sites));
    return true;
  } catch {
    return false;
  }
}

export async function fetchSites(): Promise<Site[] | null> {
  if (isDoltBackend()) return null;
  if (!isSupabaseConfigured()) return null;
  const { data, error } = await execWithRetry("fetchSites", () =>
    supabase
      .from("sites")
      .select("id, data")
      .order("updated_at", { ascending: false }),
  );
  if (error) return null;

  const sites: Site[] = [];
  for (const row of data ?? []) {
    if (row.data && row.data.id && typeof row.data.name === "string") {
      sites.push(row.data as Site);
    }
  }
  return sites.length > 0 ? sites : null;
}

export async function upsertSiteRaw(site: Site): Promise<void> {
  if (isDoltBackend()) return;
  if (!isSupabaseConfigured()) return;
  await execWithRetry("upsertSiteRaw", () =>
    supabase.from("sites").upsert(
      {
        id: site.id,
        name: site.name,
        data: site as unknown as Record<string, unknown>,
        updated_at: new Date().toISOString(),
      },
      { onConflict: "id" },
    ),
  );
}

export async function deleteSiteRemote(id: string): Promise<void> {
  if (isDoltBackend()) return;
  if (!isSupabaseConfigured()) return;
  await execWithRetry("deleteSiteRemote", () =>
    supabase.from("sites").delete().eq("id", id),
  );
}

export async function loadSites(): Promise<Site[]> {
  if (isDoltBackend()) return readDoltSites();
  const remote = await fetchSites();
  if (remote) {
    saveSitesLocal(remote);
    return remote;
  }
  return loadSitesLocal();
}

export async function fetchBuilderQuotes(): Promise<Record<
  string,
  BuilderQuote[]
> | null> {
  if (isDoltBackend()) return null;
  if (!isSupabaseConfigured()) return null;
  const { data, error } = await execWithRetry("fetchBuilderQuotes", () =>
    supabase
      .from("builder_quotes")
      .select("id, data")
      .order("updated_at", { ascending: false }),
  );
  if (error) return null;

  const quotes: Record<string, BuilderQuote[]> = {};
  for (const row of data ?? []) {
    if (row.id && Array.isArray(row.data)) {
      quotes[row.id] = row.data as BuilderQuote[];
    }
  }

  return Object.keys(quotes).length > 0 ? quotes : null;
}

export async function syncBuilderQuotes(
  scenarioId: string,
  quotes: BuilderQuote[],
): Promise<void> {
  if (isDoltBackend()) return;
  if (!isSupabaseConfigured()) return;
  await execWithRetry("syncBuilderQuotes", () =>
    syncBuilderQuotesRaw(scenarioId, quotes),
  );
}

// ---------- SyncQueue ----------

export interface SyncOp {
  id: string;
  type: "scenario" | "quotes" | "version" | "site";
  /** Generic target id: scenarioId for 'scenario'/'quotes'/'version', siteId for 'site'. */
  entityId: string;
  payload: unknown;
  retries: number;
  maxRetries: number;
  nextAttempt: number;
  createdAt: number;
}

let SYNC_QUEUE_BASE_DELAY_MS = 1000;

/** Test-only helper to set SyncQueue base delay. */
export function __setSyncQueueDelayForTests(ms: number) {
  SYNC_QUEUE_BASE_DELAY_MS = ms;
}

export class SyncQueue {
  private queue: SyncOp[] = [];
  private timer: ReturnType<typeof setTimeout> | null = null;
  private processing = false;
  private readonly LS_QUEUE_KEY = "stratosfyre-sync-queue-v1";
  private readonly MAX_RETRIES = 5;

  constructor() {
    if (isDoltBackend()) return;
    this.load();
    this.schedule();
  }

  /** Number of pending operations in the queue. */
  getPendingCount(): number {
    if (isDoltBackend()) return 0;
    return this.queue.length;
  }

  /** Clear all pending operations and localStorage. */
  clear(): void {
    this.queue = [];
    if (isDoltBackend()) return;
    this.persist();
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
  }

  enqueue(type: SyncOp["type"], entityId: string, payload: unknown): void {
    if (isDoltBackend()) return;
    // Per-type dedupe rule in ONE place: scenario/quotes/site dedupe on latest write;
    // version is append-only and NEVER deduped.
    if (type !== "version") {
      this.queue = this.queue.filter(
        (op) => !(op.type === type && op.entityId === entityId),
      );
    }

    this.queue.push({
      id: crypto.randomUUID(),
      type,
      entityId,
      payload,
      retries: 0,
      maxRetries: this.MAX_RETRIES,
      nextAttempt: Date.now(),
      createdAt: Date.now(),
    });

    this.persist();
    this.schedule();
  }

  schedule(): void {
    if (isDoltBackend()) return;
    if (this.timer) return;
    if (this.queue.length === 0) return;

    const now = Date.now();
    const nextOp = this.queue.reduce((earliest, op) =>
      op.nextAttempt < earliest.nextAttempt ? op : earliest,
    );
    const delay = Math.max(0, nextOp.nextAttempt - now);

    this.timer = setTimeout(() => {
      this.timer = null;
      this.process();
    }, delay);
  }

  private async process(): Promise<void> {
    if (isDoltBackend()) {
      this.queue = [];
      return;
    }
    if (this.processing) return;
    this.processing = true;

    while (this.queue.length > 0) {
      const now = Date.now();
      const idx = this.queue.findIndex((op) => op.nextAttempt <= now);
      if (idx === -1) break;

      const op = this.queue[idx];
      this.queue.splice(idx, 1);

      const success = await this.execute(op);
      if (!success) {
        // Retryable error — re-queue with exponential backoff
        if (op.retries < op.maxRetries) {
          op.retries++;
          op.nextAttempt =
            Date.now() + SYNC_QUEUE_BASE_DELAY_MS * Math.pow(2, op.retries - 1);
          this.queue.push(op);
        } else {
          notifySyncError(
            `Sync failed (${op.type}): max retries exceeded for ${op.entityId}`,
          );
        }
      }

      this.persist();
    }

    this.processing = false;
    this.schedule();
  }

  private async execute(op: SyncOp): Promise<boolean> {
    if (isDoltBackend()) return true;
    if (!isSupabaseConfigured()) return true;

    try {
      let result;
      switch (op.type) {
        case "scenario":
          result = await upsertScenarioRaw(op.payload as Scenario);
          break;
        case "quotes": {
          const { scenarioId, quotes } = op.payload as {
            scenarioId: string;
            quotes: BuilderQuote[];
          };
          result = await syncBuilderQuotesRaw(scenarioId, quotes);
          break;
        }
        case "version":
          result = await saveVersionRaw(op.payload as VersionSaveInput);
          break;
        case "site": {
          const payload = op.payload as Site | { id: string; _deleted: true };
          if ("_deleted" in payload && payload._deleted) {
            result = await deleteSiteRemote(payload.id);
          } else {
            result = await upsertSiteRaw(payload as Site);
          }
          break;
        }
        default:
          throw new Error(`unknown SyncOp type: ${(op as SyncOp).type}`);
      }

      if (!result.error) return true;
      if (isIgnoredError(result.error)) {
        logIgnoredSyncError(op.type, result.error);
        return true;
      }
      return false;
    } catch (err) {
      // Unknown op types are programming errors — fail loudly rather than silently swallow.
      if (err instanceof Error && err.message.startsWith("unknown SyncOp type"))
        throw err;
      return false;
    }
  }

  private persist(): void {
    if (isDoltBackend()) return;
    try {
      localStorage.setItem(this.LS_QUEUE_KEY, JSON.stringify(this.queue));
    } catch {
      // private mode / quota
    }
  }

  load(): void {
    if (isDoltBackend()) {
      this.queue = [];
      return;
    }
    try {
      const raw = localStorage.getItem(this.LS_QUEUE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) {
          this.queue = parsed;
        }
      }
    } catch {
      this.queue = [];
    }
  }
}

/** Global sync queue instance. */
export const syncQueue = new SyncQueue();

// ---------- Unified load / save ----------

export async function loadPersisted(): Promise<PersistedState | null> {
  if (isDoltBackend()) {
    const payload = readDoltPersisted();
    if (!payload) return null;
    const scenarios = payload.scenarios as Scenario[];
    return { scenarios, activeId: payload.activeId ?? scenarios[0]?.id ?? "" };
  }
  // Try Supabase first
  const remote = await fetchScenarios();
  const remoteQuotes = await fetchBuilderQuotes();
  if (remote) {
    if (remoteQuotes) {
      remote.scenarios = remote.scenarios.map((s) => ({
        ...s,
        builderQuotes: remoteQuotes[s.id] ?? s.builderQuotes,
      }));
    }
    remote.scenarios = remote.scenarios.map(migrateScenarioBlob);
    saveLocal(remote.scenarios, remote.activeId);
    return remote;
  }
  // Fallback to localStorage
  const local = loadLocal();
  if (local) {
    return {
      scenarios: local.scenarios.map(migrateScenarioBlob),
      activeId: local.activeId,
    };
  }
  return null;
}

export function savePersisted(
  scenarios: Scenario[],
  activeId: string,
): boolean {
  if (isDoltBackend()) return saveDoltLocal(scenarios, activeId);
  const ok = saveLocal(scenarios, activeId);
  // Queue Supabase sync operations instead of fire-and-forget
  for (const s of scenarios) {
    syncQueue.enqueue("scenario", s.id, s);
    if (Array.isArray(s.builderQuotes)) {
      syncQueue.enqueue("quotes", s.id, {
        scenarioId: s.id,
        quotes: s.builderQuotes,
      });
    }
  }
  return ok;
}

export async function removeScenario(id: string) {
  await deleteScenarioRemote(id);
}
